<template>
  <div>
    <v-overlay v-if="pageLoading" fixed opacity="0.9" z-index="9999">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
    <v-container class="my-5">
      <v-row class="mb-5">
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold">Mis Domicilios</h1>
        <p class="text-subtitle2 grey--text">Gestiona tus domicilios en las diferentes residencias</p>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>
        <v-icon small left>mdi-home</v-icon>
        Mis Domicilios
      </v-tab>
      <v-tab>
        <v-icon small left>mdi-plus-circle</v-icon>
        Mis Solicitudes
        <v-chip small class="ml-2" v-if="pendingResidences.length > 0" color="orange">
          {{ pendingResidences.length }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Mis Domicilios -->
      <v-tab-item>
        <!-- Residencia Activa -->
        <v-row class="mb-4" v-if="activeResidence">
          <v-col cols="12">
            <div class="section-title mb-3">
              <v-icon small class="mr-2">mdi-star</v-icon>
              <span>Domicilio Activo</span>
            </div>
          </v-col>
          <v-col cols="12">
            <v-card class="residence-card active-residence">
              <v-row no-gutters>
                <v-col cols="12" sm="auto" class="d-flex align-center pa-4">
                  <div v-if="activeResidence.idHousingUnit && activeResidence.idHousingUnit.logo" class="residence-logo">
                    <v-img
                      :src="activeResidence.idHousingUnit.logo"
                      max-width="80"
                      max-height="80"
                      contain
                    />
                  </div>
                  <v-icon v-else size="80" class="grey lighten-2">mdi-home-city</v-icon>
                </v-col>
                <v-col cols="12" sm class="pa-4 d-flex flex-column justify-center">
                  <div v-if="activeResidence.idHousingUnit">
                    <h3 class="text-h6 font-weight-bold mb-1">{{ activeResidence.idHousingUnit.name }}</h3>
                    <p class="caption grey--text mb-2">{{ activeResidence.idHousingUnit.address }}</p>
                  </div>
                  <div class="mt-3">
                    <h4 class="text-subtitle2 font-weight-bold mb-2">Domicilio:</h4>
                    <p class="text-body2 mb-1">
                      <strong>{{ getHouseAddress(activeResidence.idHouse) }}</strong>
                    </p>
                    <p v-if="getHouseFullAddress(activeResidence.idHouse)" class="text-caption grey--text mb-2">
                      {{ getHouseFullAddress(activeResidence.idHouse) }}
                    </p>
                    <p v-if="getHouseMapUrl(activeResidence.idHouse)" class="mb-0">
                      <a :href="getHouseMapUrl(activeResidence.idHouse)" target="_blank" rel="noopener" class="text-decoration-none">
                        <v-icon small>mdi-map-marker</v-icon>
                        <span>Ver en mapa</span>
                      </a>
                    </p>
                  </div>
                </v-col>
                <v-col cols="12" sm="auto" class="pa-4 d-flex align-center">
                  <v-chip color="success" text-color="white" small>
                    <v-icon small left>mdi-check-circle</v-icon>
                    Activo
                  </v-chip>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Otros Domicilios -->
        <v-row class="mb-4" v-if="otherResidences.length > 0">
          <v-col cols="12">
            <div class="section-title mb-3">
              <v-icon small class="mr-2">mdi-home</v-icon>
              <span>Otros Domicilios Aprobados</span>
            </div>
            <p class="text-caption grey--text">Selecciona un domicilio para cambiar tu residencia activa</p>
          </v-col>
          <v-col cols="12">
            <v-row>
              <v-col cols="12" sm="6" md="4" v-for="residence in otherResidences" :key="residence.id">
                <v-card class="residence-card h-100 d-flex flex-column">
                  <div v-if="residence.idHousingUnit && residence.idHousingUnit.logo" class="residence-logo-small">
                    <v-img
                      :src="residence.idHousingUnit.logo"
                      max-width="60"
                      max-height="60"
                      contain
                    />
                  </div>
                  <v-icon v-else size="60" class="grey lighten-2">mdi-home-city</v-icon>

                  <v-card-text class="flex-grow-1">
                    <h4 v-if="residence.idHousingUnit" class="text-subtitle2 font-weight-bold mb-2">
                      {{ residence.idHousingUnit.name }}
                    </h4>
                    <p class="text-body2 font-weight-bold mb-1">
                      {{ getHouseAddress(residence.idHouse) }}
                    </p>
                    <p v-if="getHouseFullAddress(residence.idHouse)" class="text-caption grey--text">
                      {{ getHouseFullAddress(residence.idHouse) }}
                    </p>
                  </v-card-text>

                  <v-card-actions>
                    <v-btn
                      color="primary"
                      small
                      block
                      @click="switchResidence(residence.id)"
                      :loading="switching"
                    >
                      Activar
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-if="!activeResidence && otherResidences.length === 0">
          <v-col cols="12">
            <v-alert type="info" outlined>
              <v-icon small left>mdi-information</v-icon>
              No tienes domicilios aprobados
            </v-alert>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Tab 2: Mis Solicitudes -->
      <v-tab-item>
        <!-- Solicitar Nuevo Domicilio -->
        <v-row class="mb-6">
          <v-col cols="12">
            <div class="section-title mb-3">
              <v-icon small class="mr-2">mdi-plus-circle</v-icon>
              <span>Solicitar Nuevo Domicilio</span>
            </div>
          </v-col>
          <v-col cols="12">
            <v-card class="pa-6">
              <v-row>
                <v-col cols="12">
                  <p class="text-subtitle2 font-weight-bold mb-3">1. Selecciona una residencial</p>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="selectedHousingUnit"
                    :items="housingUnits"
                    item-text="name"
                    item-value="id"
                    label="Selecciona una residencial"
                    outlined
                    @change="onHousingUnitChange"
                    return-object
                  >
                    <template v-slot:selection="{ item }">
                      <v-avatar size="24" :key="item.id" class="mr-2" v-if="item.logo">
                        <v-img :src="item.logo" />
                      </v-avatar>
                      {{ item.name }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-avatar size="32" :key="item.id" class="mr-2" v-if="item.logo">
                        <v-img :src="item.logo" />
                      </v-avatar>
                      <div>
                        <div><strong>{{ item.name }}</strong></div>
                        <div class="caption grey--text">{{ item.address }}</div>
                      </div>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <v-row v-if="selectedHousingUnit" class="mt-4">
                <v-col cols="12">
                  <v-divider class="mb-4"></v-divider>
                  <p class="text-subtitle2 font-weight-bold mb-3">2. Selecciona un domicilio disponible</p>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="selectedHouseId"
                    :items="unhabitedHousesByUnit"
                    item-value="id"
                    label="Selecciona una casa"
                    outlined
                    :loading="isLoading"
                  >
                    <template v-slot:selection="{ item }">
                      <v-icon small left>mdi-home</v-icon>
                      {{ item.street }} #{{ item.number }}
                    </template>
                    <template v-slot:item="{ item }">
                      <div class="full-width">
                        <strong>{{ item.street }} #{{ item.number }}</strong>
                        <br v-if="item.address">
                        <span v-if="item.address" class="caption grey--text">{{ item.address }}</span>
                      </div>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-btn
                    color="success"
                    :disabled="!selectedHouseId || requesting"
                    @click="requestNewDomicile"
                    :loading="requesting"
                    block
                    large
                  >
                    <v-icon left>mdi-check</v-icon>
                    Solicitar Domicilio
                  </v-btn>
                </v-col>
              </v-row>

              <v-row v-if="error || successMessage">
                <v-col cols="12">
                  <v-alert v-if="error" type="error" dismissible @input="error = null" class="mt-4">
                    {{ error }}
                  </v-alert>
                  <v-alert v-if="successMessage" type="success" dismissible @input="successMessage = null" class="mt-4">
                    {{ successMessage }}
                  </v-alert>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Domicilios en Solicitud -->
        <v-row>
          <v-col cols="12">
            <div class="section-title mb-3">
              <v-icon small class="mr-2">mdi-clock-outline</v-icon>
              <span>Domicilios en Solicitud</span>
            </div>
            <p class="text-caption grey--text">Estado de tus solicitudes de nuevos domicilios</p>
          </v-col>
          <v-col cols="12" v-if="pendingResidences.length > 0">
            <v-row>
              <v-col cols="12" sm="6" md="4" v-for="residence in pendingResidences" :key="residence.id">
                <v-card class="residence-card pending-residence h-100 d-flex flex-column">
                  <div v-if="residence.idHousingUnit && residence.idHousingUnit.logo" class="residence-logo-small">
                    <v-img
                      :src="residence.idHousingUnit.logo"
                      max-width="60"
                      max-height="60"
                      contain
                    />
                  </div>
                  <v-icon v-else size="60" class="grey lighten-2">mdi-home-city</v-icon>

                  <v-card-text class="flex-grow-1">
                    <h4 v-if="residence.idHousingUnit" class="text-subtitle2 font-weight-bold mb-2">
                      {{ residence.idHousingUnit.name }}
                    </h4>
                    <p class="text-body2 font-weight-bold mb-1">
                      {{ getHouseAddress(residence.idHouse) }}
                    </p>
                    <p v-if="getHouseFullAddress(residence.idHouse)" class="text-caption grey--text">
                      {{ getHouseFullAddress(residence.idHouse) }}
                    </p>
                  </v-card-text>

                  <v-card-actions>
                    <v-chip small outlined color="warning" text-color="warning" block>
                      <v-icon small left>mdi-information-outline</v-icon>
                      Pendiente de aprobación
                    </v-chip>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" v-else>
            <v-alert type="info" outlined>
              <v-icon small left>mdi-check-circle</v-icon>
              No tienes solicitudes pendientes
            </v-alert>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    </v-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ChangeResidence',
  data () {
    return {
      activeTab: 0,
      selectedHousingUnit: null,
      selectedHouseId: null,
      switching: false,
      requesting: false,
      error: null,
      successMessage: null,
      pageLoading: true
    }
  },
  computed: {
    ...mapGetters({
      residences: 'getResidences',
      availableHouses: 'getAvailableHouses',
      housingUnits: 'getHousingUnits',
      unhabitedHousesByUnit: 'getUnhabitedHousesByUnit',
      activeResidence: 'getActiveResidence',
      unhabitedHouses: 'getUnhabitedHouses',
      pendingResidences: 'getMyPendingHouseRequests',
      isLoading: 'isLoading'
    }),
    otherResidences () {
      return this.residences.filter(r => !r.is_active_context)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    ...mapActions({
      fetchMyResidences: 'fetchMyResidences',
      fetchAvailableHouses: 'fetchAvailableHouses',
      fetchHousingUnits: 'fetchHousingUnits',
      fetchAvailableHousesByUnit: 'fetchAvailableHousesByUnit',
      fetchMyPendingHouseRequests: 'fetchMyPendingHouseRequests',
      switchResidenceAction: 'switchResidence',
      requestAddHouseAction: 'requestAddHouse'
    }),
    async loadData () {
      this.pageLoading = true
      try {
        await this.fetchMyResidences()
        await this.fetchHousingUnits()
        await this.fetchMyPendingHouseRequests()
      } finally {
        this.pageLoading = false
      }
    },
    async onHousingUnitChange () {
      if (!this.selectedHousingUnit) {
        this.selectedHouseId = null
        return
      }
      this.selectedHouseId = null
      const unitId = this.selectedHousingUnit.id || this.selectedHousingUnit
      await this.fetchAvailableHousesByUnit(unitId)
    },
    async switchResidence (residentId) {
      this.switching = true
      this.pageLoading = true
      this.error = null
      try {
        await this.switchResidenceAction(residentId)
        this.successMessage = 'Tu domicilio activo ha sido cambiado exitosamente'
        setTimeout(() => {
          location.reload()
        }, 1500)
      } catch (err) {
        this.pageLoading = false
        const errorMessage = (err.response && err.response.data && err.response.data.error) || 'Error al cambiar el domicilio'
        if (errorMessage.includes('morosidad')) {
          this.error = errorMessage + '. Por favor contacte al administrador.'
        } else {
          this.error = errorMessage
        }
      } finally {
        this.switching = false
      }
    },
    async requestNewDomicile () {
      if (!this.selectedHouseId) return

      this.requesting = true
      this.error = null
      try {
        await this.requestAddHouseAction(this.selectedHouseId)
        this.successMessage = 'Tu solicitud fue enviada correctamente. Espera la aprobación del administrador.'
        this.selectedHouseId = null
        await this.loadData()
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || 'Error al solicitar el domicilio'
      } finally {
        this.requesting = false
      }
    },
    resolveHouse (houseIdOrObj) {
      if (!houseIdOrObj) return null
      if (typeof houseIdOrObj === 'object') return houseIdOrObj
      return this.availableHouses.find(h => h.id === houseIdOrObj) || null
    },
    getHouseAddress (houseIdOrObj) {
      const house = this.resolveHouse(houseIdOrObj)
      if (!house) return 'Casa desconocida'
      return `${house.street} #${house.number}`
    },
    getHouseFullAddress (houseIdOrObj) {
      const house = this.resolveHouse(houseIdOrObj)
      return house && house.address ? house.address : ''
    },
    getHouseMapUrl (houseIdOrObj) {
      const house = this.resolveHouse(houseIdOrObj)
      return house && house.mapURL ? house.mapURL : ''
    }
  }
}
</script>

<style scoped>
.full-width {
  width: 100%;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1976d2;
}

.residence-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.residence-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.residence-card.active-residence {
  border-color: #4caf50;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, transparent 100%);
}

.residence-card.pending-residence {
  opacity: 0.9;
  border-left: 4px solid #ff9800;
}

.residence-logo {
  background: white;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  min-height: 100px;
}

.residence-logo-small {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  background: #f5f5f5;
}

.v-card__text {
  word-break: break-word;
}

@media (max-width: 600px) {
  .residence-card {
    margin-bottom: 12px;
  }

  .section-title {
    font-size: 1rem;
  }
}

::v-deep .v-tabs {
  background-color: transparent;
}

::v-deep .v-tabs-bar {
  border-bottom: 2px solid #e0e0e0;
}

::v-deep .v-tab {
  text-transform: none;
  font-size: 0.95rem;
}

::v-deep .v-tab--active {
  color: #1976d2 !important;
}
</style>
